import React from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

interface UsernameGenerationFeedbackProps {
  isGenerating: boolean;
  generatedUsername?: string;
  isOriginal?: boolean;
  alternatives?: string[];
  className?: string;
}

/**
 * Component to show feedback during username generation process
 */
export const UsernameGenerationFeedback: React.FC<UsernameGenerationFeedbackProps> = ({
  isGenerating,
  generatedUsername,
  isOriginal,
  alternatives = [],
  className = ''
}) => {
  // Don't show anything if not generating and no username generated
  if (!isGenerating && !generatedUsername) {
    return null;
  }

  // Show loading state
  if (isGenerating) {
    return (
      <Alert className={`animate-fadeIn ${className}`}>
        <Loader2 className="h-4 w-4 animate-spin" />
        <AlertDescription>
          <div className="flex items-center">
            <span>Checking username availability...</span>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  // Show success state for original username
  if (generatedUsername && isOriginal) {
    return (
      <Alert className={`animate-fadeIn border-green-200 bg-green-50 ${className}`}>
        <CheckCircle className="h-4 w-4 text-green-600" />
        <AlertDescription>
          <div className="text-green-800">
            <p className="font-medium">Username available!</p>
            <p className="text-sm mt-1">
              Your username will be: <span className="font-mono font-medium">{generatedUsername}</span>
            </p>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  // Show alternative username state
  if (generatedUsername && !isOriginal) {
    return (
      <Alert className={`animate-fadeIn border-blue-200 bg-blue-50 ${className}`}>
        <AlertCircle className="h-4 w-4 text-blue-600" />
        <AlertDescription>
          <div className="text-blue-800">
            <p className="font-medium">Username adjusted</p>
            <p className="text-sm mt-1">
              Your preferred username was taken, so we've assigned: <span className="font-mono font-medium">{generatedUsername}</span>
            </p>
            {alternatives.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-blue-600">Other available options were:</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {alternatives.slice(0, 3).map((alt, index) => (
                    <span key={index} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-mono">
                      {alt}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <p className="text-xs mt-2 text-blue-600">
              💡 You can change your username later in your profile settings
            </p>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  return null;
};

/**
 * Simplified username feedback for inline display
 */
export const SimpleUsernameStatus: React.FC<{
  isGenerating: boolean;
  generatedUsername?: string;
  isOriginal?: boolean;
  className?: string;
}> = ({ isGenerating, generatedUsername, isOriginal, className = '' }) => {
  if (isGenerating) {
    return (
      <div className={`flex items-center text-sm text-gray-600 ${className}`}>
        <Loader2 className="h-3 w-3 animate-spin mr-2" />
        <span>Checking availability...</span>
      </div>
    );
  }

  if (generatedUsername && isOriginal) {
    return (
      <div className={`flex items-center text-sm text-green-600 ${className}`}>
        <CheckCircle className="h-3 w-3 mr-2" />
        <span>Username: <span className="font-mono">{generatedUsername}</span></span>
      </div>
    );
  }

  if (generatedUsername && !isOriginal) {
    return (
      <div className={`flex items-center text-sm text-blue-600 ${className}`}>
        <AlertCircle className="h-3 w-3 mr-2" />
        <span>Username: <span className="font-mono">{generatedUsername}</span> (adjusted)</span>
      </div>
    );
  }

  return null;
};

export default UsernameGenerationFeedback;
